import { noop } from 'lodash';
import { createContext } from 'react';
import * as React from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  AIOnboardingModalSuggestionSavedResult,
  AIOnboardingModalSuggestionsPagination,
  AIOnboardingModalVariant,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AIOnboardingModalFormShapeKeys } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import {
  AiOnboardingModal_AiSuggestionFormDataFragment,
  AiOnboardingModal_AiSuggestionFragment,
  AiOnboardingModal_HouseholdFragment,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import type { KGV2NodeForGrouping } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { HouseholdBillableKind } from '@/types/schema';

export interface AIOnboardingModalContextShape {
  isOpen: boolean;
  onClose: () => void;
  isOperationInFlight: boolean;
  beginOperation: () => boolean;
  endOperation: () => void;
  variant: AIOnboardingModalVariant;
  loading: boolean;
  household: AiOnboardingModal_HouseholdFragment;
  suggestionsPagination: AIOnboardingModalSuggestionsPagination;

  /**
   * The unacknowledged onboarding suggestions for the user to review by form path.
   */
  suggestionsByFormPath: Record<
    AIOnboardingModalFormShapeKeys,
    AiOnboardingModal_AiSuggestionFragment[]
  >;

  /**
   * Mapped suggestions by ID, regardless of acceptance status. Includes the
   * whole submission scope, so entries for suggestions outside the current
   * page carry form data but no evidence.
   */
  suggestionsByID: Record<string, AiSuggestionForReviewCard>;

  /**
   * Every pending suggestion in the modal's query scope, across all pages.
   * Confirm/Confirm all submits this whole set, not just the current page.
   */
  suggestionsForSubmission: AiOnboardingModal_AiSuggestionFormDataFragment[];

  /**
   * KG V2 nodes referenced by the onboarding entities' `principalNodeIDs`,
   * keyed by node ID. Used to resolve each grantor/owner/donor directly to its
   * data-model record via `resolvePrincipalsFromNodeMap`.
   */
  principalNodesById: Map<string, KGV2NodeForGrouping>;

  /** Whether the principal-node fetch is in flight. */
  principalNodesLoading: boolean;

  reviewedSuggestionIDs: string[];
  onComplete: () => void;
  onSuggestionSaved: (
    suggestionID: string
  ) => AIOnboardingModalSuggestionSavedResult;

  footerRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Optional flag to skip navigation after completing the modal.
   * When true, the modal will simply close without navigating.
   */
  skipNavigationOnComplete?: boolean;
}

export const getDefaultValues = (): AIOnboardingModalContextShape => ({
  isOpen: false,
  onClose: noop,
  isOperationInFlight: false,
  beginOperation: () => true,
  endOperation: noop,
  loading: true,
  variant: AIOnboardingModalVariant.ALL_ENTITIES,
  household: {
    id: '',
    possiblePrimaryClients: [],
    possibleGrantors: [],
    billableKind: HouseholdBillableKind.Billable,
    isTwoGrantor: false,
    documents: [],
    entities: { edges: [] },
  },
  suggestionsPagination: {
    currentPage: 0,
    pageSize: 1,
    totalCount: 0,
    currentPageCount: 0,
    reviewedCountBeforeCurrentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    onNextPage: noop,
    onPreviousPage: noop,
  },
  suggestionsByFormPath: {
    individuals: [],
    organizations: [],
    entities: [],
    testamentaryEntities: [],
    assets: [],
    liabilities: [],
    lifeInsurancePolicies: [],
  },
  suggestionsByID: {},
  suggestionsForSubmission: [],
  principalNodesById: new Map(),
  principalNodesLoading: false,
  reviewedSuggestionIDs: [],
  onComplete: noop,
  onSuggestionSaved: () => ({ isFinalSuggestion: false }),
  footerRef: React.createRef<HTMLDivElement>(),
  skipNavigationOnComplete: undefined,
});

export const AIOnboardingModalContext =
  createContext<AIOnboardingModalContextShape>(getDefaultValues());

export const useAIOnboardingModalContext = () => {
  return useGuardedContext(
    AIOnboardingModalContext,
    'AIOnboardingModalProvider'
  );
};
